import {createContext, useCallback, useContext, useMemo} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import { UserRoleEnum } from "../enums/UserRoleEnum";

interface IAuthContext {
    user: any,
    login: (data: any) => void,
    logout: () => void,
    isSuperAdmin: () => boolean,
    isOrganizationAdmin: () => boolean,
}

const AuthContext = createContext<IAuthContext>({
    user: null,
    login: () => {},
    logout: () => {},
    isSuperAdmin: () => false,
    isOrganizationAdmin: () => false,
});

export const AuthProvider = ({ children }: any) => {
    const [user, setUser] = useLocalStorage("user", null);
    const navigate = useNavigate();

    const login = useCallback(async (data: any) => {
        setUser(data);
        navigate("/dashboard/events", { replace: true });
    }, [navigate, setUser]);

    const logout = useCallback(() => {
        setUser(null);
        navigate("/", { replace: true });
    }, [navigate, setUser]);

    const isSuperAdmin = useCallback(() => {
        return user?.roles?.includes(UserRoleEnum.SuperAdmin) || false;
    }, [user]);

    const isOrganizationAdmin = useCallback(() => {
        return user?.roles?.includes(UserRoleEnum.OrganisationAdmin) || false;
    }, [user]);

    const value = useMemo(() => ({
        user,
        login,
        logout,
        isSuperAdmin,
        isOrganizationAdmin,
    }), [login, logout, user, isSuperAdmin, isOrganizationAdmin]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    return useContext(AuthContext);
};